import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, CurrencyEur, FileText,
  Image as ImageIcon, ListChecks, Sparkle, Spinner, WarningCircle,
  PencilSimple, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { api, apiError, notifyError } from "@/lib/api";
import PortalCompare from "@/components/PortalCompare";
import PriceHintBox from "@/components/PriceHintBox";
// iter v33 r62 · User: dopo "Genera annuncio" l'app portava dentro subito
// il PropertyDetail, che restava in skeleton "Sto aprendo l'annuncio..."
// finche' l'ai_description background non finiva → sembra bloccata. Nuovo
// flusso: marca l'annuncio come "in creazione" in localStorage, torna in
// Home, e il banner globale (CreatingAnnuncioBanner) mostra il progresso.
// Quando pronto → agente tocca "Apri" → entra nell'annuncio gia' completo.
import { markAnnuncioInCreazione } from "@/components/CreatingAnnuncioBanner";
import SectionCoachmark from "@/components/SectionCoachmark";
import { CONDITIONS, CONDITION_LABELS, fmtEur, fileUrl } from "./shared";

// iter v28 r427 — Code review point 4: CONVERT_STEPS top-level const stabile,
// così useEffect non lo include come dependency (era ricreato ad ogni render).
const CONVERT_STEPS = [
  "Sto raccogliendo quello che hai detto…",
  "Sto scrivendo titolo e descrizione…",
  "Sto classificando le foto per stanza…",
  "Sto stimando il prezzo di mercato…",
  "Sto preparando i contenuti del kit…",
  "Ci siamo quasi…",
];

// ── Phase 3: Review ──────────────────────────────────────────────
function ReviewPhase({ session, finalResult, onBack, onConvert, targetPropertyId }) {
  const ex = finalResult.extracted || {};
  const pe = finalResult.price_estimate;
  const [form, setForm] = useState({
    title: ex.title || "",
    property_type: (ex.property_type || session.property_type || "appartamento").toLowerCase(),
    // iter v28 r393 — User M1302: se l'agente dice "affitto", l'AI lo
    // estrae in ex.listing_type. Default = "vendita".
    listing_type: (ex.listing_type === "affitto" ? "affitto" : "vendita"),
    surface: ex.surface_sqm ?? "",
    rooms: ex.rooms ?? "",
    bathrooms: ex.bathrooms ?? "",
    floor: ex.floor != null ? String(ex.floor) : "",
    // iter v32 r50 — User M1459: rispetta SEMPRE il prezzo dichiarato a voce.
    // Prima il form era pre-popolato SOLO con la stima OMI ignorando il prezzo
    // detto dall'agente ("ho detto anche il prezzo, lui mette un prezzo suo").
    // Ora: ex.price (estratto dal vocale) > pe.estimated (stima OMI) > vuoto.
    price: ex.price != null && ex.price !== "" ? Number(ex.price) : (pe?.estimated ? Math.round(pe.estimated) : ""),
    // iter 287 — popolano da ex (estratti dal vocale) con fallback su session
    // (form iniziale, se compilato). Prima erano hardcoded "" → bug segnalato
    // dall'utente: "Via San Tommaso 41" finiva nel titolo invece che qui.
    address: ex.address || session.address || "",
    civic_number: ex.civic_number || session.civic_number || "",
    city: ex.city || session.city || "",
    condition: ex.condition || "buono",
    notes: ex.notes || "",
    // iter v32 r53 — User M1465: «i dati del proprietario nel sopralluogo
    // quand'è che li inserisco? Devi chiedermelo». Aggiunti 3 campi
    // proprietario al form, default vuoti. Se compilati, salvati direttamente
    // sull'annuncio via to-property → personalizzano email-proprietario.txt
    // nel pacchetto download.
    owner_name: "",
    owner_email: "",
    owner_phone: "",
  });
  const [features, setFeatures] = useState(ex.features || []);
  const [converting, setConverting] = useState(false);

  // iter v33 r51 · User: «se schiaccio di nuovo Genera mi dice gia'
  // generato». Se al mount la sessione ha gia' `property_id`, NON
  // mostriamo il pulsante Crea (che confonde) ma partiamo subito con
  // il redirect all'annuncio esistente. Zero attriti.
  useEffect(() => {
    const existingPid = session?.property_id;
    if (existingPid && typeof onConvert === "function") {
      onConvert(existingPid, false, { goHome: false });
    }
  }, [session?.property_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // iter v28 r375 — User M1285: dopo aver creato l'annuncio, mostriamo una
  // schermata di successo con bottone "Scarica pacchetto completo" (zip
  // con foto migliorate + descrizione + social + email + link vetrina).
  // Solo dopo che l'utente clicca "Vai all'annuncio" oppure "Scarica
  // pacchetto" e chiude, partiamo con la navigate verso PropertyDetail.
  // iter v29 r140 — Aggiunto pendingStyledPhotos per il routing all'editor
  // foto quando ci sono foto marcate "stage" o "restyle" che richiedono
  // la scelta dello stile (moderno/scandinavo/classico/etc).
  // iter v33 r3 — RIMOSSI gli stati `created` e `downloadingKit`: la
  // schermata di successo è stata rimpiazzata dal CreatingAnnuncioBanner
  // globale che gestisce lo stato in background.
  // iter v28 r302 — Loading overlay con progresso "finto ma reale":
  // step ruotanti ogni ~4s + progress bar che cresce. Senza questo
  // l'utente vede solo lo spinner del bottone per 30-60s e pensa
  // che si sia bloccato. User feedback (24/05 audio): «se ci impiega
  // tanto mettiamo una banda di caricamento».
  // iter v32 r25 — User: «"Sto creando l'annuncio" sembra impallato».
  // Aggiungo: contatore secondi reale, disclaimer dopo 30s, cancel dopo 60s.
  const [progressStep, setProgressStep] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  useEffect(() => {
    if (!converting) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProgressStep(0);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setElapsedSec(0);
      return;
    }
    const stepId = setInterval(() => {
      setProgressStep((s) => Math.min(s + 1, CONVERT_STEPS.length - 1));
    }, 3500);
    const tickId = setInterval(() => {
      setElapsedSec((s) => s + 1);
    }, 1000);
    return () => {
      clearInterval(stepId);
      clearInterval(tickId);
    };
  }, [converting]);
  // Count photos accumulated during the session — they will be attached to
  // the new property by the backend `/to-property` endpoint.
  const photoCount = React.useMemo(() => {
    let n = 0;
    const rooms = session?.rooms || {};
    Object.values(rooms).forEach((r) => { n += (r?.photo_ids?.length || 0); });
    return n;
  }, [session]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const removeFeature = (i) => setFeatures((xs) => xs.filter((_, j) => j !== i));
  const addFeature = (v) => { const t = (v || "").trim(); if (t) setFeatures((xs) => [...xs, t]); };

  // iter v33 r26 · User: «crea l'annuncio e poi faccio io migliora tutte le
  // foto a mano, non in automatico, perche' se non funziona non andiamo a
  // capo». Zero automatismo: il sopralluogo crea SOLO l'annuncio + salva
  // le foto originali. Nessun kit_worker in background, nessuna push, nessun
  // flag "in creazione". L'agente entra nell'annuncio, va sul tab Foto e
  // preme "Migliora tutte" quando vuole lui.
  const autoOutputs = React.useMemo(() => {
    return [];
  }, []);

  const convert = async () => {
    setConverting(true);
    // iter v33 r58 · Registro il momento del click per poter cercare
    // annunci "creati DOPO questo istante" quando le altre strategie
    // falliscono (safety net nuclear).
    const sessionStartTs = Date.now();

    // iter v33 r55 · Helper: verifica se il backend ha gia' creato l'annuncio
    // per questa sessione, indipendentemente da cosa e' successo lato client
    // (network drop, iOS in background, response persa). Se si', naviga
    // dentro. Ritorna true se ha trovato+navigato l'annuncio, false altrimenti.
    const checkBackendAndNavigate = async () => {
      try {
        const { data: sd } = await api.get(`/sopralluogo/${session.id}`, { timeout: 15000 });
        if (sd?.property_id && typeof onConvert === "function") {
          onConvert(sd.property_id, false, { goHome: false });
          setConverting(false);
          return true;
        }
      } catch (_) { /* silent */ }
      return false;
    };

    // iter v33 r58 · SAFETY NET NUCLEAR: se `checkBackendAndNavigate` fallisce
    // (backend vecchio, endpoint non risponde, ecc.), controlliamo la lista
    // globale annunci `/api/properties`. Se troviamo un annuncio creato negli
    // ultimi N minuti che matcha questo sopralluogo (per titolo, prezzo o
    // indirizzo), ci navighiamo comunque. Questo funziona ANCHE quando il
    // backend e' quello vecchio senza il fix del lock r57.
    const checkPropertiesListAndNavigate = async () => {
      try {
        const { data } = await api.get(`/properties`, { timeout: 15000 });
        const items = Array.isArray(data) ? data : (data?.items || []);
        // Cerca un annuncio creato negli ultimi 5 minuti che sembri "questo"
        const cutoffMs = sessionStartTs - 60_000; // margine 1 min prima del click
        const now = Date.now();
        for (const p of items) {
          // Parse created_at (ISO string) → ms
          let createdMs = 0;
          try { createdMs = new Date(p.created_at).getTime(); } catch (_) { /* invalid date, skip */ }
          if (!createdMs || createdMs < cutoffMs || createdMs > now + 60_000) continue;
          // Match euristico: sopralluogo id, o titolo (starts with — il backend
          // appende " · <città>"), o combinazione indirizzo+città identici.
          const titleForm = (form.title || "").trim();
          const titleProp = (p.title || "").trim();
          const addressForm = (form.address || "").trim().toLowerCase();
          const addressProp = (p.address || "").trim().toLowerCase();
          const cityForm = (form.city || "").trim().toLowerCase();
          const cityProp = (p.city || "").trim().toLowerCase();
          const matchesSession =
            (p.from_sopralluogo === session.id) ||
            (p.sopralluogo_session_id === session.id) ||
            (titleForm && titleProp && (titleProp === titleForm || titleProp.startsWith(titleForm))) ||
            (addressForm && addressForm === addressProp && cityForm === cityProp);
          if (matchesSession && typeof onConvert === "function") {
            onConvert(p.id, false, { goHome: false });
            setConverting(false);
            return true;
          }
        }
      } catch (_) { /* silent */ }
      return false;
    };

    // Combina le due strategie in un unico check.
    const findExistingAnnuncioAndNavigate = async () => {
      if (await checkBackendAndNavigate()) return true;
      if (await checkPropertiesListAndNavigate()) return true;
      return false;
    };

    try {
      const body = {
        title: form.title || "Nuovo immobile",
        property_type: form.property_type.charAt(0).toUpperCase() + form.property_type.slice(1),
        // iter v28 r393 — User M1302: invia listing_type al backend.
        listing_type: form.listing_type || "vendita",
        price: form.price !== "" ? Number(form.price) : null,
        surface: form.surface !== "" ? Number(form.surface) : null,
        rooms: form.rooms !== "" ? parseInt(form.rooms, 10) : null,
        bathrooms: form.bathrooms !== "" ? parseInt(form.bathrooms, 10) : null,
        floor: form.floor || null,
        address: form.address || null,
        civic_number: form.civic_number || null,
        city: form.city || null,
        features,
        notes: form.notes || "",
        owner_name: (form.owner_name || "").trim() || null,
        owner_email: (form.owner_email || "").trim() || null,
        owner_phone: (form.owner_phone || "").trim() || null,
        target_property_id: targetPropertyId || null,
      };
      const { data } = await api.post(`/sopralluogo/${session.id}/to-property`, body, {
        // iter v33 r52 · User: «ci mette piu' del solito, torno indietro,
        // ripremo, mi crea 2 annunci». Il timeout globale axios era 60s,
        // ma con LLM lento (title+descrizione+servizi vicini) la chiamata
        // puo' impiegare 90-120s. Alziamo a 180s per essere sicuri che il
        // frontend NON dia mai per persa prima del backend.
        // Anche se scattasse, il lock atomico backend impedisce il doppio
        // annuncio: il secondo tentativo si accoda e restituisce lo stesso
        // property_id del primo.
        timeout: 180000,
        validateStatus: (s) => (s >= 200 && s < 300),
      });
      // iter v33 r51 · User: «me lo genera pero' mi ritorna alla schermata
      // del riepilogo, se schiaccio di nuovo genera dice gia' generato».
      // Il navigate() del parent doveva partire subito dopo la risposta
      // OK. Prima invece facevamo prima setConverting, poi toast, poi
      // onConvert → su iOS Capacitor a volte lo stato React re-renderizzava
      // la Review e il navigate scattava un ciclo dopo, dando la sensazione
      // di "torno indietro". Adesso il navigate parte PER PRIMO, gli
      // effetti collaterali (toast, cleanup) girano dopo.
      if (data?.property_id && typeof onConvert === "function") {
        // iter v33 r62 · User: «mi ha portato subito dentro la pagina
        // dell'annuncio senza barra di caricamento, sembra bloccata».
        // Marca l'annuncio come "in creazione" in localStorage e naviga
        // a Home: il CreatingAnnuncioBanner globale mostra progresso +
        // ETA, l'utente puo' liberamente navigare le altre sezioni, e
        // quando l'annuncio e' pronto tocca la pill "Apri" per entrare.
        markAnnuncioInCreazione({
          pid: data.property_id,
          title: (form.title || "").trim() || "Nuovo annuncio",
          addr: `${(form.address || "").trim()} ${(form.city || "").trim()}`.trim(),
        });
        toast.success("Sto preparando l'annuncio", {
          description: "Ti avviso qui sotto appena e' pronto. Nel frattempo usa l'app.",
          duration: 5000,
        });
        setConverting(false);
        onConvert(data.property_id, !!data.merged, { goHome: true });
        return;
      } else {
        // iter v33 r55/r58 · Response 2xx ma property_id manca (caso 202
        // lock attivo). Polliamo entrambe le strategie: check sessione
        // + check lista annunci (safety net nuclear per backend vecchi).
        let polled = false;
        for (let i = 0; i < 20; i++) {  // 20 × 3s = 60s
          await new Promise((r) => setTimeout(r, 3000));
          if (await findExistingAnnuncioAndNavigate()) { polled = true; break; }
        }
        if (!polled) {
          // iter v33 r58 · SAFETY NET NUCLEAR: invece di lasciare l'utente
          // sulla ReviewPhase (che sembra "non ho fatto niente"), lo
          // porto DIRETTAMENTE in Home dove puo' vedere subito la lista
          // annunci. Se l'annuncio e' stato creato lo vede la'; se no,
          // sa dove ripartire. Toast persistente per spiegare.
          toast.info("Vai in Home a controllare · Se l'annuncio non c'è, riprova fra un minuto", { duration: 8000 });
          setConverting(false);
          if (typeof onConvert === "function") {
            // trigger navigate: goHome: true dice al parent di andare a /dashboard
            onConvert(null, false, { goHome: true });
          }
          return;
        }
        // navigate gia' fatto dentro findExistingAnnuncioAndNavigate → esci
        return;
      }
      // NOTA r62: il ramo "success" ora ritorna prima (mark + goHome).
      // Se arriviamo qui e' perche' il response non aveva property_id
      // ma abbiamo trovato l'annuncio via polling → gia' navigato.
    } catch (e) {
      // iter v33 r55/r58 · Prima di dichiarare fallimento e chiudere
      // l'overlay, verifica SEMPRE lato backend se l'annuncio e' stato
      // creato + controlla anche la lista annunci globale (nel caso il
      // backend fosse quello vecchio senza il fix del lock r57).
      if (await findExistingAnnuncioAndNavigate()) return;
      // Ultimo tentativo: pollo per 30s con entrambe le strategie.
      for (let i = 0; i < 10; i++) {  // 10 × 3s = 30s
        await new Promise((r) => setTimeout(r, 3000));
        if (await findExistingAnnuncioAndNavigate()) return;
      }
      // iter v33 r58 · SAFETY NET NUCLEAR: dopo 30s di polling senza
      // successo, invece di lasciare l'utente sulla ReviewPhase (che
      // sembra "non ho fatto niente"), lo porto DIRETTAMENTE in Home
      // dove puo' vedere subito la lista annunci. Zero confusione.
      toast.error("Rete instabile · Vai in Home a controllare", {
        description: "Se l'annuncio non c'è, riprova fra un minuto",
        duration: 10000,
      });
      setConverting(false);
      if (typeof onConvert === "function") {
        onConvert(null, false, { goHome: true });
      }
    }
  };

  // iter v33 r3 — User M1471: rimosso `downloadKit` da qui. Lo scarico
  // pacchetto avviene SOLO dalla scheda immobile (MaterialiTab/SocialStudio)
  // quando il kit è davvero completo. Niente più ZIP con cartelle vuote.

  return (
    <div className="max-w-3xl mx-auto pb-10" data-testid="sopralluogo-review">
      {/* iter v33 r3 — RIMOSSA la schermata "Annuncio creato" full-screen.
          User M1471: «non aprirmi l'annuncio finché non è pronto, si impalla».
          La conferma è ora gestita dal banner persistente in basso
          (CreatingAnnuncioBanner) che mostra timer + foto pronte e diventa
          verde "Annuncio pronto · Apri" SOLO quando il kit è completato. */}

      {/* iter v28 r302 — Overlay full-screen durante creazione annuncio:
          step ruotanti + progress bar visiva. Risolve il "schermo vuoto"
          per 30-60s post-click Crea annuncio. */}
      {converting && (
        <div className="fixed inset-0 z-[120] bg-[#FAFAF7]/95 backdrop-blur-sm flex items-center justify-center px-6" data-testid="convert-overlay">
          <div className="max-w-md w-full text-center space-y-5">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#7C8B6E]/15">
              <Sparkle size={24} weight="fill" className="text-[#7C8B6E] animate-pulse" />
            </div>
            <h2 className="text-[20px] font-serif text-[#1C1C1A] leading-tight">
              Sto creando il tuo annuncio
            </h2>
            <div className="text-[13.5px] text-[#3F3D38] min-h-[20px] italic" data-testid="convert-step-text">
              {CONVERT_STEPS[progressStep]}
            </div>
            {/* Progress bar (determinata in base a progressStep / total) */}
            <div className="w-full bg-[#EAE7DF] rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-[#7C8B6E] transition-all duration-[4500ms] ease-out"
                style={{ width: `${Math.min(95, ((progressStep + 1) / CONVERT_STEPS.length) * 100)}%` }}
                data-testid="convert-progress-bar"
              />
            </div>
            <div className="text-[11px] text-[#9A968B] leading-snug">
              {elapsedSec >= 30
                ? "Sta richiedendo più del solito. Resta qui, sto finendo."
                : "Può richiedere fino a un minuto. Resta qui, sto facendo tutto io."}
            </div>
            <div className="text-[11px] text-[#7C8B6E] font-mono tabular-nums" data-testid="convert-elapsed">
              {elapsedSec}s
            </div>
            {photoCount > 0 && (
              <div className="text-[11px] text-[#9A968B] flex items-center justify-center gap-1.5">
                <ImageIcon size={11} /> {photoCount} foto allegate
              </div>
            )}
            {elapsedSec >= 30 && (
              <div className="flex flex-col gap-2 pt-2">
                <button
                  onClick={() => {
                    // iter v33 r58 · Escape hatch manuale: se l'utente non
                    // ne puo' piu' di aspettare, lo portiamo in Home dove
                    // vede la lista annunci. Se e' stato creato lo vede
                    // subito, altrimenti sa che deve ripartire.
                    setConverting(false);
                    toast.info("Ti porto in Home · Se l'annuncio non c'è, riprova fra un minuto", { duration: 6000 });
                    if (typeof onConvert === "function") {
                      onConvert(null, false, { goHome: true });
                    }
                  }}
                  className="text-[12px] text-[#7C8B6E] hover:text-[#1C1C1A] underline"
                  data-testid="convert-go-home-btn"
                >
                  Vai in Home a controllare
                </button>
                {elapsedSec >= 60 && (
                  <button
                    onClick={() => { setConverting(false); toast.info("Creazione annullata. Riprova quando vuoi."); }}
                    className="text-[11px] text-[#9A968B] hover:text-[#1C1C1A] underline"
                    data-testid="convert-cancel-btn"
                  >
                    Annulla e riprova
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-[12px] text-[#9A968B] hover:text-[#1C1C1A] mb-3"
        data-testid="review-back-btn"
      >
        <ArrowLeft size={13} /> Torna a registrare
      </button>

      <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">Sopralluogo · finito</div>
      <h1 className="text-[26px] lg:text-[32px] font-serif leading-[1.1] text-[#1C1C1A] mb-1">
        Ecco la <span className="italic text-[#7C8B6E]">scheda</span> dell&apos;immobile.
      </h1>
      <p className="text-[13px] text-[#6B6A65] mb-6">
        Pronto ha estratto i dati dal tuo sopralluogo. Controlla, correggi, poi <strong className="text-[#1C1C1A]">crea l&apos;annuncio</strong> — foto, descrizione e servizi della zona arrivano già pronti.
      </p>

      {/* iter v33 r8 · User M1485. Tutorial contestuale che spiega in 3
          passi cosa può fare qui. Appare UNA VOLTA sola. */}
      <SectionCoachmark
        id="sopralluogo-review"
        title="Controlla, correggi, crea l'annuncio"
        steps={[
          {
            icon: <PencilSimple size={15} weight="bold" />,
            title: "Correggi i dati",
            body: "Titolo, metri quadri, prezzo, condizioni: modifica solo quello che serve, il resto è già estratto dal vocale.",
          },
          {
            icon: <ImageIcon size={15} weight="bold" />,
            title: "Le foto sono allegate",
            body: "Le vedi qui sotto o dopo, nella scheda dell'immobile. Potrai migliorarle, ritagliarle o riordinarle.",
          },
          {
            icon: <PaperPlaneTilt size={15} weight="bold" />,
            title: "Crea annuncio · vai avanti",
            body: "L'annuncio si crea subito. Dal tab Foto della scheda immobile premi \u201cMigliora tutte\u201d quando vuoi migliorare le foto — decidi tu i tempi.",
          },
        ]}
      />

      {/* iter v32 r53 — User M1465: «mi dai due stime. La prima io la toglierei».
          Rimosso il box grande OMI/AVM in cima (era un duplicato visivo della
          stima inline accanto al prezzo). Resta solo l'helper «Usa € X» vicino
          all'input prezzo, più discreto e funzionale. */}

      {pe && form.city && (
        <div className="mb-5">
          <PortalCompare city={form.city} surface_sqm={form.surface || ex.surface_sqm} property_type={form.property_type} />
        </div>
      )}

      {/* Fallback warning */}
      {finalResult.fallback_no_voice && (
        <div className="mb-5 bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 flex gap-2 text-[12.5px]" data-testid="fallback-warning">
          <WarningCircle size={16} className="shrink-0 mt-0.5" />
          <div>Non hai registrato note vocali. I dati sono stati ricavati solo dalle foto — potrebbero essere incompleti.</div>
        </div>
      )}

      {/* Edit form */}
      <div className="bg-white border border-[#E8E5DE] rounded-xl p-5 space-y-4">
        {/* iter v28 r394 — Banner di conferma quando l'AI ha rilevato
            "affitto" dal sopralluogo. Dà fiducia all'agente: «non ti ho
            messo per sbaglio l'annuncio in vendita, ho davvero capito».
            Si auto-nasconde se l'agente cambia a "Vendita" o se l'AI non
            l'ha rilevato (ex.listing_type ≠ "affitto"). */}
        {ex.listing_type === "affitto" && form.listing_type === "affitto" && (
          <div
            className="flex items-start gap-2.5 bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-lg px-3.5 py-2.5"
            data-testid="review-affitto-detected"
          >
            <div className="w-6 h-6 rounded-full bg-[#7C8B6E] text-white inline-flex items-center justify-center shrink-0 mt-0.5">
              <Sparkle size={11} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1C1C1A] leading-tight">
                Ho sentito che è in affitto
              </div>
              <div className="text-[11.5px] text-[#5C6B4E] mt-0.5 leading-snug">
                Ti ho impostato il campo prezzo come canone mensile. Puoi sempre cambiarlo a vendita qui sotto.
              </div>
            </div>
          </div>
        )}
        {/* iter v28 r393 — User M1302: scelta Vendita/Affitto in evidenza.
            Pre-popolato da AI extraction (se l'agente ha detto "affitto" in
            sopralluogo) ma sempre modificabile a mano. È un'informazione
            decisiva per portali ed estensione, quindi è prima del titolo. */}
        <div>
          <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1.5">Tipo annuncio</label>
          <div className="inline-flex rounded-full bg-[#F1F4ED] border border-[#E8E5DE] p-1" role="tablist">
            {[
              { v: "vendita", l: "Vendita" },
              { v: "affitto", l: "Affitto" },
            ].map(({ v, l }) => {
              const active = form.listing_type === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => update("listing_type", v)}
                  className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-all ${
                    active ? "bg-[#1C1C1A] text-white shadow-sm" : "text-[#5C6B4E] hover:bg-[#E5E7E0]"
                  }`}
                  data-testid={`review-listing-type-${v}`}
                  aria-pressed={active}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Titolo</label>
          <input
            value={form.title} onChange={(e) => update("title", e.target.value)}
            className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
            data-testid="review-title"
          />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ["surface", "Superficie (m²)", "number"],
            ["rooms", "Locali", "number"],
            ["bathrooms", "Bagni", "number"],
            ["floor", "Piano", "text"],
          ].map(([k, lab, tp]) => (
            <div key={k}>
              <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">{lab}</label>
              <input
                type={tp} value={form[k]} onChange={(e) => update(k, e.target.value)}
                className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid={`review-${k}`}
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Città</label>
            <input
              value={form.city} onChange={(e) => update("city", e.target.value)}
              className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
              data-testid="review-city"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Indirizzo</label>
            {/* iter v28 r425 — via + civico in 2 campi separati */}
            <div className="grid grid-cols-[1fr_92px] gap-2">
              <input
                value={form.address} onChange={(e) => update("address", e.target.value)}
                placeholder="Via / Piazza"
                className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="review-address"
              />
              <input
                value={form.civic_number || ""} onChange={(e) => update("civic_number", e.target.value)}
                placeholder="Civico"
                className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="review-civic"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">
              {form.listing_type === "affitto" ? "Canone mensile (€)" : "Prezzo richiesta (€)"}
            </label>
            <input
              type="number" value={form.price} onChange={(e) => update("price", e.target.value)}
              placeholder={pe ? String(Math.round(pe.estimated)) : (form.listing_type === "affitto" ? "es. 1.200" : "—")}
              className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
              data-testid="review-price"
            />
            {/* iter v28 r402 — User M1311: stima di mercato compatta sotto
                al campo prezzo. Cambia se passi da vendita ad affitto. */}
            <div className="mt-2">
              <PriceHintBox
                listingType={form.listing_type}
                city={form.city}
                surface={form.surface}
                propertyType={form.property_type}
                state={form.condition}
                currentPrice={form.price}
                onUsePrice={(v) => update("price", String(v))}
              />
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Stato conservazione</label>
            <select
              value={form.condition} onChange={(e) => update("condition", e.target.value)}
              className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] bg-white focus:outline-none focus:border-[#7C8B6E]"
              data-testid="review-condition"
            >
              {CONDITIONS.map((c) => <option key={c} value={c}>{CONDITION_LABELS[c]}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Pregi / caratteristiche</label>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="review-features">
            {features.map((f, i) => (
              <span key={`${f}-${i}`} className="inline-flex items-center gap-1 bg-[#EDEAE2] text-[#3F3D38] text-[12px] rounded-full px-2.5 py-1">
                {f}
                <button onClick={() => removeFeature(i)} className="text-[#9A968B] hover:text-red-600" aria-label="rimuovi">×</button>
              </span>
            ))}
          </div>
          <input
            type="text" placeholder="Aggiungi (Invio per confermare)"
            onKeyDown={(e) => { if (e.key === "Enter" && e.currentTarget.value.trim()) { addFeature(e.currentTarget.value); e.currentTarget.value = ""; } }}
            className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[13px] focus:outline-none focus:border-[#7C8B6E]"
            data-testid="review-features-input"
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Note operative</label>
          <textarea
            rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)}
            className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[13.5px] leading-[1.5] focus:outline-none focus:border-[#7C8B6E]"
            data-testid="review-notes"
          />
        </div>

        {/* iter v32 r53 — User M1465: «i dati del proprietario nel sopralluogo
            quand'è che li inserisco? Devi chiedermelo». Sezione opzionale ma
            ben visibile prima di "Crea annuncio". Se l'agente li compila qui,
            l'email-proprietario.txt nel pacchetto è già personalizzata col
            nome di battesimo del cliente. Se no, resta «Gentile cliente». */}
        <div className="border-t border-[#E8E5DE] pt-4 mt-2">
          <div className="flex items-center gap-2 mb-2.5">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B]">Dati proprietario</div>
            <span className="text-[10.5px] text-[#9A968B] italic">opzionali, ma personalizzano l&apos;email pronta</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Nome e cognome</label>
              <input
                type="text"
                value={form.owner_name}
                onChange={(e) => update("owner_name", e.target.value)}
                placeholder="Es. Giulia Bianchi"
                className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="review-owner-name"
              />
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Email</label>
              <input
                type="email"
                value={form.owner_email}
                onChange={(e) => update("owner_email", e.target.value)}
                placeholder="proprietario@email.it"
                autoCapitalize="off"
                autoCorrect="off"
                className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="review-owner-email"
              />
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Telefono</label>
              <input
                type="tel"
                value={form.owner_phone}
                onChange={(e) => update("owner_phone", e.target.value)}
                placeholder="+39 ..."
                className="w-full px-3 py-2 border border-[#E8E5DE] rounded-md text-[14px] focus:outline-none focus:border-[#7C8B6E]"
                data-testid="review-owner-phone"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Defects */}
      {(ex.defects || []).length > 0 && (
        <div className="mt-4 bg-white border border-[#E8E5DE] rounded-xl p-5" data-testid="review-defects">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-2">
            <WarningCircle size={13} /> Attenzioni rilevate
          </div>
          <ul className="space-y-1.5 text-[13px] text-[#3F3D38]">
            {ex.defects.map((d, i) => (
              <li key={`def-${i}-${(d || '').slice(0, 24)}`} className="flex gap-2">
                <span className="text-amber-600 mt-1">•</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Docs to request */}
      {(ex.docs_to_request || []).length > 0 && (
        <div className="mt-4 bg-white border border-[#E8E5DE] rounded-xl p-5" data-testid="review-docs">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-2">
            <ListChecks size={13} /> Documenti da richiedere al venditore
          </div>
          <ul className="space-y-1.5 text-[13px] text-[#3F3D38]">
            {ex.docs_to_request.map((d, i) => (
              <li key={`doc-${i}-${(d || '').slice(0, 24)}`} className="flex gap-2">
                <Check size={13} className="text-[#7C8B6E] mt-1 shrink-0" weight="bold" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Transcript full (collapsible) */}
      {finalResult.transcript_full && (
        <details className="mt-4 bg-white border border-[#E8E5DE] rounded-xl p-5" data-testid="review-transcript">
          <summary className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] cursor-pointer">
            <FileText size={13} /> Vedi la trascrizione completa
          </summary>
          <div className="mt-3 text-[13px] text-[#3F3D38] leading-[1.6] whitespace-pre-wrap">
            {finalResult.transcript_full}
          </div>
        </details>
      )}

      {/* iter v33 r4 — RIMOSSA la card "Cosa vuoi che prepari ora?".
          iter v33 r28 — Aggiornato: il sopralluogo ORA crea SOLO
          l'annuncio con le foto originali. Miglioramento foto, prima/dopo,
          link vetrina, post social vengono creati on-demand dalle rispettive
          tab dell'annuncio quando l'agente li vuole. */}

      {/* Convert CTA */}
      <div className="mt-6">
        {photoCount > 0 && (
          <div className="bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-md p-3 mb-3 flex items-start gap-2.5" data-testid="convert-photos-info">
            <ImageIcon size={16} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-[#3F3D38] leading-snug">
              <strong>{photoCount} foto</strong> del sopralluogo verranno allegate all&apos;annuncio.
              <span className="text-[#6B6A65]"> Per migliorarle vai sul tab Foto e premi <em>Migliora tutte</em>.</span>
            </div>
          </div>
        )}
        {/* iter v33 r28 · convert-time-hint RIMOSSO: la creazione annuncio ora
            e' istantanea (~2s), non c'e' piu' un worker automatico che
            richiede attesa. L'agente entra subito nell'annuncio. */}
        <button
          onClick={convert}
          disabled={converting}
          className="w-full bg-[#7C8B6E] hover:bg-[#6B7A5D] disabled:opacity-60 text-white font-medium py-3.5 rounded-md flex items-center justify-center gap-2 transition-colors"
          data-testid="convert-btn"
        >
          {converting ? (
            <><Spinner size={16} className="animate-spin" /> Creo l&apos;annuncio…</>
          ) : (
            <><Check size={16} weight="bold" /> Crea annuncio{photoCount > 0 ? ` con ${photoCount} foto` : ""} <ArrowRight size={14} weight="bold" /></>
          )}
        </button>
      </div>
    </div>
  );
}


export default ReviewPhase;
